import {
  Box,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreIcon from '@material-ui/icons/MoreVert';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

import TableSkeleton from '../../../components/TableSkeleton';
import { useFlowMutation, useFlows } from '../../../hooks/queries/useOnboarding';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useBackdrop from '../../../hooks/useBackdrop';
import useSnackbar from '../../../hooks/useSnackbar';
import useTablePagination from '../../../hooks/useTablePagination';
import { IOnboardingFlow } from '../../../services/types';
import { RouteNames } from '../../config';

interface IProps {
  onRowClick: (flow: IOnboardingFlow) => void;
  onDelete: (flow: IOnboardingFlow) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  idTableCell: {
    width: 60,
  },
  deleteCell: {
    width: 110,
  },
  statusCell: {
    width: 120,
  },
  dateCell: {
    width: 210,
  },
  flowKeyCell: {
    width: 210,
  },
  createdAtTableCell: {
    width: 180,
  },
  tableRow: {
    height: 52,
  },
}));

const FlowList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { onRowClick, onDelete } = props;

  const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination();
  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const { data, isLoading } = useFlows(page, rowsPerPage);
  const { remove, update, duplicate } = useFlowMutation();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuFocusedFlowRef = useRef<IOnboardingFlow>();

  const flows = data?.items;
  const totalCount = data?.totalCount;

  const handleRowClick = (flow: IOnboardingFlow) => () => onRowClick(flow);

  const handleDeleteClick = (flow: IOnboardingFlow) => {
    confirm.open({
      onProceed: async () => {
        await remove
          .mutateAsync(flow)
          .then(() => {
            onDelete(flow);
          })
          .catch(e => {
            console.log(e);
          });
      },
    });
  };

  const handleStatusToggle = (flow: IOnboardingFlow) => {
    confirm.open({
      onProceed: async () => {
        const patchData: Partial<IOnboardingFlow> = { id: flow.id, isActive: !flow.isActive };
        await update.mutateAsync(patchData).catch(e => {
          console.log(e);
        });
      },
    });
  };

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onDuplicateFlow = useCallback(
    (flow: IOnboardingFlow) => {
      const onProceed = async () => {
        try {
          backdrop.show();
          await duplicate.mutateAsync(flow);

          snackbar.success('Duplicated successfully.');
        } catch (e) {
          snackbar.error(`An error has occurred. ${e instanceof Error ? e.message : e.statusText}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: `Duplicate ${flow.title}`,
        text: 'Are you sure you want to make a copy of this flow?',
        onProceed,
      });
    },
    [backdrop, snackbar, confirm, duplicate],
  );

  return (
    <Box>
      <Paper>
        <Table>
          <TableHead>
            <TableCell align="center" padding="none" className={classes.idTableCell}>
              ID
            </TableCell>
            <TableCell>Title</TableCell>
            <TableCell className={classes.flowKeyCell}>Deeplink Flow Key</TableCell>
            <TableCell className={classes.dateCell}>Updated At</TableCell>
            <TableCell align="center" className={classes.statusCell}>
              Status
            </TableCell>
            <TableCell className={classes.deleteCell}>Delete</TableCell>
          </TableHead>
          <TableBody>
            {isLoading && <TableSkeleton rows={rowsPerPage} columns={6} />}
            {!isLoading &&
              flows &&
              flows.map(flow => {
                const { id, title, flowKey, isActive, updatedAt } = flow;
                return (
                  <TableRow
                    key={id}
                    component={Link}
                    to={`${RouteNames.OnboardingFlow}/v/${id}`}
                    hover
                    onClick={handleRowClick(flow)}
                    className={classes.tableRow}
                  >
                    <TableCell align="center" padding="none">
                      {id}
                    </TableCell>
                    <TableCell>{title}</TableCell>
                    <TableCell>{flowKey}</TableCell>
                    <TableCell>{moment(updatedAt).format('llll')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={isActive ? 'Activated' : 'Deactivated'} placement="bottom">
                        <Switch
                          color={'primary'}
                          checked={isActive}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            handleStatusToggle(flow);
                          }}
                          value={flow.id}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color={'primary'}
                        disableFocusRipple={true}
                        disableTouchRipple={true}
                        disableRipple={true}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(flow);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        style={{ padding: '12px 14px' }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          menuFocusedFlowRef.current = flow;
                          handleMoreMenuClick(e);
                        }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {!isLoading && isEmpty(flows) && (
          <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body1">Flow does not exist. Please try again.</Typography>
          </Box>
        )}
        {totalCount !== undefined && (
          <TablePagination
            count={totalCount || -1} // an unknown number of items, provide -1. (https://material-ui.com/api/table-pagination/)
            rowsPerPage={rowsPerPage}
            page={page - 1} // TablePagination has the zero-based index of the current page.
            onChangePage={(_: unknown, p: number) => setPage(p + 1)}
            onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
              const rpp = parseInt(e.target.value, 10);
              setRowsPerPage(rpp);
              setPage(1);
            }}
          />
        )}
        <Menu anchorEl={menuAnchorEl} keepMounted open={Boolean(menuAnchorEl)} onClose={handleMoreMenuClose}>
          <MenuItem
            onClick={() => {
              if (onDuplicateFlow && menuFocusedFlowRef.current) {
                onDuplicateFlow(menuFocusedFlowRef.current);
                setMenuAnchorEl(null);
              }
            }}
          >
            Duplicate
          </MenuItem>
        </Menu>
      </Paper>
    </Box>
  );
};

export default FlowList;
